// src/utils/firebase.js
// Firebase Cloud Messaging initialization and utilities

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Initialize Firebase
let app;
let messaging = null;

try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error('Firebase initialization error:', error);
}

/**
 * Initialize Firebase Messaging
 * Must be called after checking browser support
 */
export async function initializeMessaging() {
  try {
    const supported = await isSupported();
    if (!supported) {
      console.warn('Firebase Messaging is not supported in this browser');
      return null;
    }
    
    if (!messaging && app) {
      messaging = getMessaging(app);
    }
    
    return messaging;
  } catch (error) {
    console.error('Error initializing messaging:', error);
    return null;
  }
}

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null
 */
export async function requestNotificationPermission() {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    // Initialize messaging
    const msg = await initializeMessaging();
    if (!msg) return null;

    // Register service worker
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      
      // انتظار حتى يصبح الـ SW فعالاً
      if (registration.installing) {
        await new Promise((resolve) => {
          registration.installing.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') resolve();
          });
        });
      }
    } catch (swError) {
      console.error('Service Worker registration failed:', swError);
      return null;
    }

    // Get FCM token - مع أو بدون VAPID key
    const tokenOptions = {
      serviceWorkerRegistration: registration,
    };
    
    // إضافة VAPID key فقط إذا كان موجوداً
    if (VAPID_KEY) {
      tokenOptions.vapidKey = VAPID_KEY;
    }

    const token = await getToken(msg, tokenOptions);

    if (token) {
      console.log('FCM Token obtained:', token.substring(0, 20) + '...');
      return token;
    } else {
      console.warn('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/**
 * Register FCM token with backend
 * @param {string} fcmToken - FCM device token
 * @param {string} authToken - User auth JWT token
 * @param {string} backendUrl - Backend API URL
 */
export async function registerTokenWithBackend(fcmToken, authToken, backendUrl) {
  try {
    const response = await fetch(`${backendUrl}/api/fcm/register-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        token: fcmToken,
        device_type: 'web',
        device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Web' : 'Desktop Web',
      }),
    });

    if (response.ok) {
      const data = await response.json();
      console.log('FCM token registered with backend:', data);
      return data;
    } else {
      console.error('Failed to register FCM token:', response.status);
      return null;
    }
  } catch (error) {
    console.error('Error registering FCM token:', error);
    return null;
  }
}

/**
 * Listen for foreground messages
 * @param {Function} callback - Function to call when message received
 * @returns {Function|null} Unsubscribe function
 */
export async function onForegroundMessage(callback) {
  try {
    const msg = await initializeMessaging();
    if (!msg) return null;

    return onMessage(msg, (payload) => {
      console.log('Foreground message received:', payload);
      // Data-only messages: البيانات كلها في payload.data
      // Notification messages: البيانات في payload.notification + payload.data
      callback(payload);
    });
  } catch (error) {
    console.error('Error setting up foreground listener:', error);
    return null;
  }
}

/**
 * Unregister FCM token from backend
 * @param {string} fcmToken 
 * @param {string} authToken 
 * @param {string} backendUrl 
 */
export async function unregisterToken(fcmToken, authToken, backendUrl) {
  try {
    const response = await fetch(`${backendUrl}/api/fcm/unregister-token?token=${encodeURIComponent(fcmToken)}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    });
    return response.ok;
  } catch (error) {
    console.error('Error unregistering token:', error);
    return false;
  }
}

export { app, messaging };
